import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import { FiCheckCircle, FiArrowRight } from '../../components/Icons';

const GettingStartedPage: React.FC = () => {
  const steps = [
    {
      title: 'Set Up Your Account',
      description: 'Complete your profile and company information',
      articles: [
        { title: 'First Login & Setup', slug: 'getting-started/getting-started-guide' },
        { title: 'Company Profile Setup', slug: 'getting-started/company-profile' },
        { title: 'User Settings', slug: 'admin/user-settings' }
      ]
    },
    {
      title: 'Add Your Customers',
      description: 'Import existing customers or add them manually',
      articles: [
        { title: 'Adding New Customers', slug: 'customers/customer-management-guide' },
        { title: 'Importing via CSV', slug: 'customers/importing-customers-csv' },
        { title: 'Customer Tags & Categories', slug: 'customers/customer-organization' }
      ]
    },
    {
      title: 'Configure Services & Pricing',
      description: 'Set up your service offerings and pricing structure',
      articles: [
        { title: 'Service Items Setup', slug: 'services/service-management-guide' },
        { title: 'Pricing Configuration', slug: 'services/pricing-setup' },
        { title: 'Service Packages', slug: 'services/service-packages' }
      ]
    },
    {
      title: 'Start Scheduling',
      description: 'Book your first appointments and set up your calendar',
      articles: [
        { title: 'Creating Appointments', slug: 'appointments/appointment-scheduling-guide' },
        { title: 'Calendar Setup', slug: 'appointments/calendar-configuration' },
        { title: 'Recurring Appointments', slug: 'appointments/recurring-appointments' }
      ]
    },
    {
      title: 'Generate Invoices',
      description: 'Create professional invoices and get paid faster',
      articles: [
        { title: 'Creating Invoices', slug: 'finances/invoicing-billing-guide' },
        { title: 'Payment Processing', slug: 'finances/payment-processing' },
        { title: 'Stripe Setup', slug: 'finances/stripe-integration' }
      ]
    }
  ];

  const quickLinks = [
    { icon: '📱', title: 'Mobile Access', description: 'Use the CRM on your phone or tablet' },
    { icon: '🔄', title: 'Calendar Sync', description: 'Connect Google or Outlook calendar' },
    { icon: '👥', title: 'Add Team Members', description: 'Invite employees to the system' },
    { icon: '📊', title: 'View Reports', description: 'Track your business performance' },
    { icon: '🔔', title: 'Set Up Notifications', description: 'Configure alerts and reminders' },
    { icon: '💬', title: 'Get Support', description: 'Contact our support team' }
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Getting Started with Your CRM
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Follow this step-by-step guide to set up your CRM and start managing your business more efficiently.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Setup Checklist</h2>
          <div className="space-y-8">
            {steps.map((step, index) => (
              <div key={index} className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                    {index + 1}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {step.description}
                  </p>
                  <div className="space-y-2">
                    {step.articles.map((article, articleIndex) => (
                      <Link
                        key={articleIndex}
                        to={`/articles/${article.slug}`}
                        className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                      >
                        <FiArrowRight className="h-4 w-4" />
                        <span>{article.title}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quickLinks.map((link, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start space-x-4">
                  <div className="text-3xl">{link.icon}</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {link.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {link.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Video Tutorials */}
        <div className="bg-blue-50 rounded-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Video Tutorials</h2>
          <p className="text-gray-600 mb-6">
            Watch our video tutorials to learn the basics quickly:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-4">
              <div className="bg-gray-200 h-32 rounded mb-3 flex items-center justify-center">
                <span className="text-gray-500">📹 Video Placeholder</span>
              </div>
              <h4 className="font-medium text-gray-900">CRM Overview</h4>
              <p className="text-sm text-gray-600">5 min</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="bg-gray-200 h-32 rounded mb-3 flex items-center justify-center">
                <span className="text-gray-500">📹 Video Placeholder</span>
              </div>
              <h4 className="font-medium text-gray-900">Creating Your First Invoice</h4>
              <p className="text-sm text-gray-600">8 min</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="bg-gray-200 h-32 rounded mb-3 flex items-center justify-center">
                <span className="text-gray-500">📹 Video Placeholder</span>
              </div>
              <h4 className="font-medium text-gray-900">Scheduling Appointments</h4>
              <p className="text-sm text-gray-600">6 min</p>
            </div>
          </div>
        </div>

        {/* Support Section */}
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Need Help?</h2>
          <p className="text-gray-600 mb-6">
            Our support team is here to help you get started and answer any questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Contact Support
            </Link>
            <a
              href="mailto:support@definedcleaning.com"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-colors"
            >
              Email Us
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default GettingStartedPage;